import { useEffect } from 'react';

// Canonical SEO origin — all canonical/OG URLs point at the production domain so
// preview/staging never competes for ranking with the live site.
export const SITE_ORIGIN = 'https://peershare.net';

function upsertMeta(attr, key, content) {
  if (!content) return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
}

/**
 * Sets per-page SEO head tags (title, description, canonical, OG/Twitter) and
 * injects JSON-LD structured data. Restores the previous title and removes the
 * injected JSON-LD on unmount so SPA navigation stays clean.
 */
export function useSeo({ title, description, path = '', image, jsonLd = [], robots }) {
  useEffect(() => {
    const prevTitle = document.title;
    const url = SITE_ORIGIN + path;
    if (title) document.title = title;

    upsertMeta('name', 'description', description);
    upsertLink('canonical', url);
    if (robots) upsertMeta('name', 'robots', robots);

    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', 'article');
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:site_name', 'Peershare เปียแชร์');
    if (image) upsertMeta('property', 'og:image', image);
    upsertMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    if (image) upsertMeta('name', 'twitter:image', image);

    const scripts = (jsonLd || []).filter(Boolean).map((obj) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo', '1');
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      document.title = prevTitle;
      scripts.forEach((s) => s.remove());
      if (robots) {
        const r = document.head.querySelector('meta[name="robots"]');
        if (r) r.remove();
      }
    };
  }, [title, description, path, image, robots, JSON.stringify(jsonLd)]);
}
